import React, { useContext, useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import {
  ProfileActionsRow,
  ProfileActionsTd,
  ProfileActionsTh,
  ProfileNameTd,
  ProfileNameTh,
  ProfilePermissionTd,
  ProfilePermissionTh,
  SwitchBox,
  SwitchDiv,
  SwitchesDiv,
  SwitchInput,
  SwitchSlider,
} from './style'
import { getHeadersByTenant, getPermissionsByTenant } from '../utils'
import { useTenant } from '../../../../contexts/TenantContext'
import axios from '../../../../config/axios'
import UserContext from '../../../../contexts/UserContext'
import { useProfileContext } from '../../../../contexts/AccessesContext'
import trash from '../../../../assets/assesses/trash.svg'
import editPen from '../../../../assets/edit-pen.svg'
import ModalContext from '../../../../contexts/ModalContext'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import OrdenoLoading from '../../../../components/OrdenoLoading'
import { GlobalTable, HeaderLine, Td, Tr, FooterTr } from '../../../../components/styles/table-style'
import { useOutletContext } from 'react-router-dom'
import { PeopleOutletType } from '..'

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: auto;
`

interface Profile {
  id: number
  name: string
  admin: boolean
  permissions: string[]
  createdAt: string
  updatedAt: string
}

const TableProfile = () => {
  const { profiles, setProfiles, setDeletedProfile, fetchProfilesWithoutAdmin, loadQuantity, setLoadQuantity, hasMoreProfiles, totalProfiles } = useProfileContext()
  const { refresh, setLoadingHeader } = useOutletContext<PeopleOutletType>()
  const [checkedState, setCheckedState] = useState<{ [key: string]: boolean }>({})
  const { setModalActive, setModalSection, setEditingProfile } = useContext(ModalContext)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const { user } = useContext(UserContext)
  const { tenantId } = useTenant()

  if (tenantId === null) {
    return <OrdenoLoading variant='fullscreen' text='Carregando...' />
  }

  const headerProfilesTable = getHeadersByTenant(tenantId)
  const allPermissions = getPermissionsByTenant(tenantId)
  const lastColIndex = headerProfilesTable.length - 1

  const headerCell = (label: string, index: number) => {
    if (index === 0) return <ProfileNameTh key={index}>{label}</ProfileNameTh>
    if (index === lastColIndex) return <ProfileActionsTh key={index}>{label}</ProfileActionsTh>
    return <ProfilePermissionTh key={index}>{label}</ProfilePermissionTh>
  }

  const bodyCell = (permission: string, index: number, profile: Profile, cellContent: React.ReactNode) => {
    if (permission === 'name') {
      return <ProfileNameTd key={index}>{cellContent}</ProfileNameTd>
    }
    if (permission === 'delete') {
      return <ProfileActionsTd key={index}>{cellContent}</ProfileActionsTd>
    }
    return <ProfilePermissionTd key={index}>{cellContent}</ProfilePermissionTd>
  }

  useEffect(() => {
    const initialCheckedState: { [key: string]: boolean } = {}

    profiles.forEach((profile) => {
      allPermissions.forEach((permission, permissionIndex) => {
        const key = `${profile.id}-${permissionIndex}`
        initialCheckedState[key] = profile.permissions.includes(permission)
      })
    })
    setCheckedState(initialCheckedState)
  }, [profiles, allPermissions])

  const isFirstRenderForProfiles = useRef(true)
  useEffect(() => {
    if (isFirstRenderForProfiles.current) {
      isFirstRenderForProfiles.current = false
      return
    }
    if (refresh) {
      setLoadingHeader?.(true)
      fetchProfilesWithoutAdmin().finally(() => setLoadingHeader?.(false))
    }
  }, [refresh])

  const handleDelete = async (id: number) => {
    setDeletedProfile(id)
    setModalActive(true)
    setModalSection('deleteProfile')
  }

  const handleEditProfileName = (profile: Profile) => {
    setEditingProfile({ id: profile.id, name: profile.name })
    setModalSection('createProfile')
    setModalActive(true)
  }

  const updateProfilePermissions = async (profileId: number, updatedPermissions: string[]) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/profile/${profileId}`,
        { permissions: updatedPermissions }
      )
    } catch (error) {
      console.error('Erro ao atualizar permissões', error)
    }
  }

  const handleCheckboxChange = (profileId: number, permissionIndex: number) => {
    const key = `${profileId}-${permissionIndex}`
    const permissionName = allPermissions[permissionIndex]

    setCheckedState((prevCheckedState) => {
      const newCheckedState = {
        ...prevCheckedState,
        [key]: !prevCheckedState[key],
      }

      const profile = profiles.find(p => p.id === profileId)
      if (!profile) return prevCheckedState

      if (newCheckedState[key]) {
        if (!profile.permissions.includes(permissionName)) {
          profile.permissions.push(permissionName)
        }
      } else {
        profile.permissions = profile.permissions.filter((perm) => perm !== permissionName)
      }

      updateProfilePermissions(profile.id, profile.permissions)

      return newCheckedState
    })
  }

  const itemsOfTable = (permissionType: string, profile: Profile, permissionIndex: number) => {
    if (permissionType === 'delete') {
      return (
        <ProfileActionsRow>
          <img
            src={editPen}
            alt="Editar nome do perfil"
            onClick={() => handleEditProfileName(profile)}
          />
          <img src={trash} alt="Excluir perfil" onClick={() => handleDelete(profile.id)} className="trash" />
        </ProfileActionsRow>
      )
    }

    return (
      <SwitchesDiv>
        <SwitchDiv>
          <SwitchBox>
            <SwitchInput
              type="checkbox"
              checked={checkedState[`${profile.id}-${permissionIndex}`] || false}
              onChange={() => handleCheckboxChange(profile.id, permissionIndex)}
            />
            <SwitchSlider className="slider"></SwitchSlider>
          </SwitchBox>
        </SwitchDiv>
      </SwitchesDiv>
    )
  }

  return (
    <>
      <TableWrapper>
        <GlobalTable>
          <thead>
            <HeaderLine>
              {headerProfilesTable.map((item, indexHeader) => headerCell(item, indexHeader))}
            </HeaderLine>
          </thead>
          <tbody>
            {profiles.map((profile) => (
              <Tr key={profile.id}>
                {allPermissions.map((permission, indexPermissions) =>
                  bodyCell(
                    permission,
                    indexPermissions,
                    profile,
                    permission === 'name' ? (
                      <>{profile.name}</>
                    ) : (
                      itemsOfTable(permission, profile, indexPermissions)
                    )
                  )
                )}
              </Tr>
            ))}
          </tbody>
          <tfoot>
            <FooterTr>
              <Td colSpan={headerProfilesTable.length} style={{ textAlign: 'center', background: 'transparent' }}>
                <LoadMoreButton
                  currentCount={profiles.length}
                  totalCount={totalProfiles ?? undefined}
                  onLoadMore={() => setLoadQuantity(prev => prev + 50)}
                  hasMore={hasMoreProfiles}
                />
              </Td>
            </FooterTr>
          </tfoot>
        </GlobalTable>
      </TableWrapper>
    </>
  )
}

export default TableProfile

